import { TLocale } from '@internal/utils/machine/i18n/TLocale'
import { SystemPageKey } from 'page/SystemPageKey'
import { ParsedUrlQuery } from 'querystring'
import slugify from 'slugify'
import {
  routingTable,
  searchRoute,
  TCompareMachinesRouteParams,
  TDashboardRouteParams,
  TMachineDetailsRouteParams,
  TSearchResultsPageRouteParams,
} from '../routingTable'
import { getSlugifyOptions } from '../slugs/getSlugifyOptions'
import { TRoute } from '../TRoute'
import { queryParamsToString } from './queryParamsToString'
import { removeDoubleSlashes } from './removeDoubleSlashes'

type TParams =
  | {
      key: SystemPageKey.StartPage
    }
  | ({
      key: SystemPageKey.Dashboard
    } & (
      | {
          userId?: string
          machineGroupId?: string
          inspection?: boolean
          dashboardRoute?: undefined
        }
      | {
          userId?: undefined
          machineGroupId?: undefined
          inspection?: undefined
          dashboardRoute: string
        }
    ))
  | {
      key: SystemPageKey.SearchResults
      machineTypeSlug?: string
      machineTypeName?: string
      brandSlug?: string | null
      modelSlug?: string | null
      queryParams?: TSearchResultsPageRouteParams['queryParams']
    }
  | ({
      key: SystemPageKey.PDP
    } & TMachineDetailsRouteParams)
  | {
      key: 'slug'
      slug: string
      queryParams?: ParsedUrlQuery
    }
  | {
      key: 'url'
      url: string
      queryParams?: ParsedUrlQuery
    }
  | ({
      key: SystemPageKey.CompareMachines
    } & TCompareMachinesRouteParams)

export const urlFor = (locale: TLocale, params: TParams | string) => {
  if (typeof params === 'string') {
    const slug = params
    return removeDoubleSlashes(`/${locale}/${slug}/`)
  }

  if (params.key === 'slug') {
    const url = removeDoubleSlashes(`/${locale}/${params.slug}/`)
    const queryString =
      params.queryParams && queryParamsToString(params.queryParams)
    return `${url}${queryString && `?${queryString}`}`
  }

  if (params.key === 'url') {
    const queryString =
      params.queryParams && queryParamsToString(params.queryParams)
    return `${params.url}${queryString ? `?${queryString}` : ''}`
  }

  if (params.key === SystemPageKey.SearchResults) {
    let machineTypeSlug = params.machineTypeSlug ?? searchRoute[locale]
    if (params.machineTypeName) {
      machineTypeSlug = slugify(
        params.machineTypeName,
        getSlugifyOptions(locale)
      )
    }
    const route = routingTable.systemPageRoutes[SystemPageKey.SearchResults][
      locale
    ] as TRoute<TSearchResultsPageRouteParams>

    return route.compile({
      pathParams: {
        machineTypeSlug,
        brandSlug: params.brandSlug ?? undefined,
        modelSlug: params.modelSlug ?? undefined,
      },
      queryParams: params.queryParams ?? {},
    })
  }

  if (params.key === SystemPageKey.Dashboard) {
    const route = routingTable.systemPageRoutes[SystemPageKey.Dashboard][
      locale
    ] as TRoute<TDashboardRouteParams>

    const dashboardUrl = route.compile({
      pathParams: {},
      queryParams: {},
    })

    const elements: string[] = []
    if (params.userId) {
      elements.push(params.userId)
    }

    if (params.machineGroupId) {
      elements.push(`machine/${params.machineGroupId.toLowerCase()}`)
    }

    if (params.inspection) {
      elements.push('inspection')
    }
    const dashboardRoute = params.dashboardRoute
      ? `#${params.dashboardRoute}`
      : elements.length > 0
        ? `#/${elements.join('/')}/`
        : ''
    return `${dashboardUrl}${dashboardRoute}`
  }

  return routingTable.systemPageRoutes[params.key][locale].compile(params)
}
